import React, { useCallback, useMemo } from "react";
import { memo } from "react";

// internal-deps
import { default as EditIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/edit/16";
import { default as CloseIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/clearFilled/16";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { useEffectOnce } from "@swiggy-private/core-ui";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// components
import { COOKING_REQUESTS, EDIT_COOKING_REQUESTS } from "./Constants";
import {
    ButtonStyled,
    EndIconStyled,
    StartIconStyled,
    InstructionTextContainer,
} from "./CartRestaurantSuggestion.style";

export interface CartRestaurantSuggestionProps {
    className?: string;
    restaurantSuggestion?: string;
    placeholder?: string;
    inputChangeCallback?: (note: string) => void;
    onClick: (isClosed?: boolean) => void;
    onImpression: VoidFunction;
    isOptedInRx: boolean;
    note: string;
    textAreaActive: boolean;
    onButtonClick: VoidFunction;
    title?: string;
    isRxInstructionsHalfCardXPEnabled?: boolean;
    onCookingInstructionsFocus?: () => void;
    onCookingInstructionsBlur?: () => void;
    onCookingInstructionsClick?: (element: HTMLElement) => void;
}

function CartRestaurantSuggestion(props: CartRestaurantSuggestionProps) {
    const {
        onClick,
        onButtonClick,
        note,
        textAreaActive,
        title,
        isRxInstructionsHalfCardXPEnabled,
        onCookingInstructionsClick,
    } = props;

    const instructionText = useMemo(() => {
        if (!isRxInstructionsHalfCardXPEnabled) {
            return title ?? COOKING_REQUESTS;
        }
        return note ? EDIT_COOKING_REQUESTS : (title ?? COOKING_REQUESTS);
    }, [isRxInstructionsHalfCardXPEnabled, note, title]);

    useEffectOnce(() => {
        props.onImpression();
    });

    const handleButtonClick = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
            onCookingInstructionsClick?.(e.currentTarget);
            onClick?.(!!note);
            onButtonClick();
        },
        [onClick, note, onButtonClick, onCookingInstructionsClick],
    );

    return (
        <>
            <ButtonStyled isActive={!textAreaActive && Boolean(note)} onClick={handleButtonClick}>
                {(isRxInstructionsHalfCardXPEnabled || textAreaActive || !note) && (
                    <StartIconStyled as="span">
                        <GenericIcon Icon={EditIcon} strokeColor="text_Med_Emphasis" fillColor="text_Med_Emphasis" />
                    </StartIconStyled>
                )}

                <InstructionTextContainer>
                    <Text color="text_Med_Emphasis" variant="V2.body3.neutral.medium">
                        {instructionText}
                    </Text>
                </InstructionTextContainer>

                {!isRxInstructionsHalfCardXPEnabled && !textAreaActive && note && (
                    <EndIconStyled as="span">
                        <GenericIcon Icon={CloseIcon} strokeColor="divider" fillColor="divider" />
                    </EndIconStyled>
                )}
            </ButtonStyled>
        </>
    );
}

export default memo(CartRestaurantSuggestion);
